// « Mon compte » — /parameters for a team member (role employee). Kevin,
// 29/10: « settings for employee should only be about changing password ».
// Identity from GET /api/users/user (employee shape: name, email); one card,
// the password, sent to POST /api/auth/change-password
// { current_password, new_password }. 200 → toast, fields cleared, the person
// stays signed in; 400 / 429 → the server's `detail` in the form.
// Passwords live only in this component's state: never logged, never stored,
// never in a URL.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Banner, Card, Field, Spinner, Toast, initialsOf } from '../design/ui';
import './SettingsPage.css';

export const PASSWORD_MIN_LENGTH = 8;
// bcrypt reads at most 72 bytes: the server refuses a longer password.
export const PASSWORD_MAX_BYTES = 72;

export const utf8Bytes = (value) => {
  const text = String(value || '');
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text).length;
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0);
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
};

// What keeps the form from being sent, per field. Empty fields are not errors
// (nothing typed yet): they only keep the button disabled.
export const passwordProblems = ({ current, next, confirm }) => {
  const problems = {};
  if (next) {
    if (next.length < PASSWORD_MIN_LENGTH) problems.next = 'too_short';
    else if (utf8Bytes(next) > PASSWORD_MAX_BYTES) problems.next = 'too_long';
    else if (current && next === current) problems.next = 'same';
  }
  if (confirm && next && confirm !== next) problems.confirm = 'mismatch';
  return problems;
};

export const passwordFormReady = (form) => Boolean(form.current && form.next && form.confirm)
  && Object.keys(passwordProblems(form)).length === 0;

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  } catch (error) {
    return {};
  }
};

const EMPTY_FORM = { current: '', next: '', confirm: '' };

const UserParameterEmployee = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(undefined); // undefined = loading
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/users/user', { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
      .then((response) => { if (!cancelled) setUser(response.data || {}); })
      .catch((error) => {
        console.error('Failed to fetch user data:', error?.response?.status);
        // The header falls back on what the login stored; the password card works without it.
        if (!cancelled) setUser(readStoredUser());
      });
    return () => { cancelled = true; };
  }, []);

  const problems = passwordProblems(form);
  const ready = passwordFormReady(form);

  const update = (key) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [key]: value }));
    setServerError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!ready || saving) return;
    setSaving(true);
    setServerError('');
    try {
      const response = await axios.post(
        '/api/auth/change-password',
        { current_password: form.current, new_password: form.next },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } },
      );
      setForm(EMPTY_FORM);
      setToast(response?.data?.message || t('accountScopeUi.account.passwordChanged', 'Mot de passe modifié.'));
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      console.error('Failed to change password:', status);
      setServerError(typeof detail === 'string' && detail
        ? detail
        : t('accountScopeUi.account.passwordFailed', 'Le mot de passe n’a pas pu être changé. Réessayez dans un instant.'));
    } finally {
      setSaving(false);
    }
  };

  const nextError = {
    too_short: t('accountScopeUi.account.tooShort', 'Au moins {{min}} caractères.', { min: PASSWORD_MIN_LENGTH }),
    too_long: t('accountScopeUi.account.tooLong', 'Trop long : {{max}} octets au maximum (un caractère accentué en compte deux).', { max: PASSWORD_MAX_BYTES }),
    same: t('accountScopeUi.account.sameAsCurrent', 'Le nouveau mot de passe doit être différent de l’actuel.'),
  }[problems.next];
  const confirmError = problems.confirm
    ? t('settingsPage.passwordMismatch', 'Les deux nouveaux mots de passe ne correspondent pas.')
    : null;

  const name = user?.name || [user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.email || '';
  const roleLabel = t('sidebarNav.roleEmployee', 'Équipe');

  return (
    <div className="u-page u-page--narrow settings-v2">
      <section className="u-card settings-v2__hero settings-v2__hero--plain" aria-busy={user === undefined}>
        <div className="settings-v2__identity">
          {user === undefined
            ? <span className="u-skeleton settings-v2__avatar-skeleton" aria-hidden="true" />
            : <span className="u-avatar u-avatar--lg" aria-hidden="true">{initialsOf(name)}</span>}
          <div className="settings-v2__identity-text">
            <span className="u-overline">{t('accountScopeUi.account.overline', 'Mon compte')}</span>
            {user === undefined ? (
              <span className="u-skeleton settings-v2__title-skeleton" aria-hidden="true" />
            ) : (
              <>
                <h1 className="u-page-title">{name || t('accountScopeUi.account.overline', 'Mon compte')}</h1>
                <div className="settings-v2__meta">
                  {user.email && user.email !== name && <span>{user.email}</span>}
                  <span>{roleLabel}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <div className="u-well">
        <Card
          icon={Lock}
          title={t('settingsPage.passwordTitle', 'Mot de passe')}
          description={t('settingsPage.passwordDesc', 'Le mot de passe de connexion à Unanimity.')}
          footer={(
            <button type="submit" form="account-password-form" className="u-btn u-btn--primary" disabled={!ready || saving}>
              {saving && <Spinner />}
              {t('settingsPage.changePassword', 'Changer le mot de passe')}
            </button>
          )}
          footerNote={t('accountScopeUi.account.stayConnected', 'Vous restez connecté sur cet appareil.')}
        >
          <form id="account-password-form" className="u-form settings-v2__password" onSubmit={submit} noValidate>
            {serverError && <Banner tone="danger">{serverError}</Banner>}
            <Field label={t('settingsPage.currentPassword', 'Mot de passe actuel')} htmlFor="account-pw-current">
              <input
                id="account-pw-current"
                type="password"
                className="u-input"
                value={form.current}
                onChange={update('current')}
                autoComplete="current-password"
                required
              />
            </Field>
            <Field
              label={t('settingsPage.newPassword', 'Nouveau mot de passe')}
              htmlFor="account-pw-new"
              help={t('accountScopeUi.account.rule', 'Au moins {{min}} caractères, différent de l’actuel.', { min: PASSWORD_MIN_LENGTH })}
              error={nextError}
            >
              <input
                id="account-pw-new"
                type="password"
                className="u-input"
                value={form.next}
                onChange={update('next')}
                autoComplete="new-password"
                aria-invalid={Boolean(nextError)}
                required
              />
            </Field>
            <Field label={t('settingsPage.confirmPassword', 'Confirmer le nouveau mot de passe')} htmlFor="account-pw-confirm" error={confirmError}>
              <input
                id="account-pw-confirm"
                type="password"
                className="u-input"
                value={form.confirm}
                onChange={update('confirm')}
                autoComplete="new-password"
                aria-invalid={Boolean(confirmError)}
                required
              />
            </Field>
          </form>
        </Card>
      </div>

      {toast && (
        <Toast tone="success" onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast}
        </Toast>
      )}
    </div>
  );
};

export default UserParameterEmployee;
